import type { Command } from "../document/commands.ts";
import type { WorldDocument } from "../document/types.ts";
import type { AreaOffset, AreaRect } from "./area.ts";
import type { AreaClipboard } from "./area-clipboard.ts";
import { planAreaDelete, type AreaDeleteGuard, type AreaDeletePlan } from "./area-delete.ts";
import type { AreaKept, AreaMovePlan } from "./area-move.ts";
import type { AreaPastePlan } from "./area-paste.ts";

/**
 * The area's two edits (ADR-128 decisions 31 and 33), each the planner's one compound run through the store: the
 * delete of a rectangle, which Delete and the box erase share, and a confirmed move. Each says what it did in plain
 * data for the status bar's words.
 */

/**
 * What a Delete or a box erase did: nothing to do; held back because a move of the area is pending, which is
 * confirmed or put back first; refused by the planner, with the links it would break and what leads to the arrival
 * points it would remove; dropped by the store (a session that takes no edit); or done, with the layers written and
 * what went: the cells cleared in all (`changes`) and by grid (tile cells, corners and quarter cells), and the
 * props, markers and doors removed.
 */
export type AreaDeleteOutcome =
  | { readonly kind: "nothing" }
  | { readonly kind: "moving" }
  | { readonly kind: "dropped" }
  | Extract<AreaDeletePlan, { readonly kind: "refused" }>
  | { readonly kind: "deleted"; readonly layers: readonly string[]; readonly changes: number; readonly tiles: number; readonly corners: number; readonly cells: number; readonly props: number; readonly markers: number; readonly doors: number };

/**
 * What confirming a pending move did: nothing to do, the pending move ended, `still` when that was because the
 * offset was zero and not because the layers in reach hold nothing there; refused by the planner, at the map's
 * edge, the move still pending; dropped by the store, still pending; or done, with the command run, the layers
 * written, what moved, the painted cells it replaced and on how many layers, and the things left standing at the
 * destination.
 */
export type AreaMoveOutcome =
  | { readonly kind: "nothing"; readonly still?: boolean }
  | { readonly kind: "dropped" }
  | Extract<AreaMovePlan, { readonly kind: "refused" }>
  | { readonly kind: "moved"; readonly layers: readonly string[]; readonly changes: number; readonly props: number; readonly markers: number; readonly replaced: number; readonly replacedLayers: number; readonly kept: AreaKept; readonly offset: AreaOffset };

/**
 * What copying an area did: the clipboard it kept; or nothing, because the layers in reach hold nothing there to
 * copy, with how many door cells were left out of the count (door cells are never copied, decision 38).
 */
export type AreaCopyOutcome = { readonly kind: "copied"; readonly clipboard: AreaClipboard } | { readonly kind: "nothing"; readonly doorCells: number };

/**
 * What cutting an area did: what a Delete would have said when it did not run, or the clipboard it kept and what
 * the one command removed.
 */
export type AreaCutOutcome = Exclude<AreaDeleteOutcome, { readonly kind: "deleted" }> | { readonly kind: "cut"; readonly clipboard: AreaClipboard; readonly deleted: Extract<AreaDeleteOutcome, { readonly kind: "deleted" }> };

/**
 * What putting the clipboard down began: a pending paste, or why none began (nothing is in the clipboard, the reach
 * is This layer, or the clipboard was taken from another map).
 */
export type AreaPasteStart = "started" | "empty" | "reach" | "other-map";

/**
 * What Ctrl+D did: began a paste of the copy it made, or copied nothing.
 */
export type AreaDuplicateOutcome = { readonly kind: "started" } | { readonly kind: "nothing"; readonly doorCells: number };

/**
 * What confirming a pending paste did: nothing to do, the pending paste ended; refused by the planner, at the map's
 * edge, the paste still pending; dropped by the store, still pending; or done, with the layers
 * written, what was added, the painted cells it replaced and on how many layers, the things already standing there, the clipboard's layers left behind and the block's
 * top-left tile.
 */
export type AreaPasteOutcome =
  | { readonly kind: "nothing" }
  | { readonly kind: "dropped" }
  | Extract<AreaPastePlan, { readonly kind: "refused" }>
  | { readonly kind: "pasted"; readonly layers: readonly string[]; readonly changes: number; readonly props: number; readonly markers: number; readonly replaced: number; readonly replacedLayers: number; readonly kept: AreaKept; readonly left: readonly string[]; readonly at: { readonly x: number; readonly y: number } };

/**
 * What an edit runs against: the world as it stands, the map in view and the store's one way to run a command.
 */
export interface AreaActionHost {
  readonly document: () => WorldDocument;
  readonly mapId: () => string;
  readonly run: (command: Command) => boolean;
}

/**
 * Plans and runs the delete of a rectangle against the document and the layers taken as they stand now (decision
 * 30).
 *
 * @param host What the delete runs against.
 * @param rect The rectangle.
 * @param taken The layers the reach takes.
 * @param guard What the reference check reads.
 * @returns What it did.
 */
export function deleteArea(host: AreaActionHost, rect: AreaRect, taken: readonly string[], guard: AreaDeleteGuard): AreaDeleteOutcome {
  const plan = planAreaDelete(host.document(), host.mapId(), rect, taken, guard);
  if (plan.kind !== "delete") {
    return plan;
  }
  if (!host.run(plan.command)) {
    return { kind: "dropped" };
  }
  let tiles = 0;
  let corners = 0;
  let cells = 0;
  for (const part of plan.command.commands) {
    if (part.kind === "setTiles") {
      tiles += part.cells.length;
    } else if (part.kind === "paintCorners") {
      corners += part.cells.length;
    } else if (part.kind === "paintCollision" || part.kind === "paintZone" || part.kind === "paintDoor") {
      cells += part.cells.length;
    }
  }
  return { kind: "deleted", layers: plan.layers, changes: plan.changes, tiles, corners, cells, props: plan.props, markers: plan.markers, doors: plan.doors };
}

/**
 * Runs a planned move.
 *
 * @param host What the move runs against.
 * @param plan The plan, made against the document as it stands.
 * @param offset The plan's offset.
 * @returns What it did.
 */
export function moveArea(host: AreaActionHost, plan: AreaMovePlan, offset: AreaOffset): AreaMoveOutcome {
  if (plan.kind !== "move") {
    return plan;
  }
  if (!host.run(plan.command)) {
    return { kind: "dropped" };
  }
  return { kind: "moved", layers: plan.layers, changes: plan.changes, props: plan.props, markers: plan.markers, replaced: plan.replaced.reduce((sum, entry) => sum + entry.indices.length, 0), replacedLayers: plan.replaced.length, kept: plan.kept, offset };
}

/**
 * Runs a planned paste.
 *
 * @param host What the paste runs against.
 * @param plan The plan, made against the document as it stands with the store's own allocator.
 * @param at The block's top-left tile.
 * @returns What it did.
 */
export function pasteArea(host: AreaActionHost, plan: AreaPastePlan, at: { readonly x: number; readonly y: number }): AreaPasteOutcome {
  if (plan.kind !== "paste") {
    return plan;
  }
  if (!host.run(plan.command)) {
    return { kind: "dropped" };
  }
  return { kind: "pasted", layers: plan.layers, changes: plan.changes, props: plan.props, markers: plan.markers, replaced: plan.replaced.reduce((sum, entry) => sum + entry.indices.length, 0), replacedLayers: plan.replaced.length, kept: plan.kept, left: plan.left, at };
}
